<script lang="ts">
    import { getCountryCallingCode } from 'libphonenumber-js';
    import { getFlagEmoji } from '$lib/components/_DisplayComponents/FormQuestion/helper';
    import countries from '$lib/components/_DisplayComponents/FormQuestion/countries.json';
    import translations from '../../translations';

    // Utils
    import { getBorderRadius } from '$lib/global/helper/border-radius';

    // Types
    import type { Language } from '$lib/types/Language';
    import type { CountryCode } from 'libphonenumber-js';

    export let open = false;
    export let choices: string[] = [];
    export let selected = 'de';
    export let value = '';
    export let language: Language = 'de';
    export let handleSelect: (event: MouseEvent, option: CountryCode) => void;
    export let autoFocusSearchField = false;

    let filterInputElement;
    let dropdownElement: HTMLDivElement;
    let filteredChoices: string[] = [];
    let filter = '';

    const scrollToSelectedCountry = () => {
        if (dropdownElement) {
            const scrollableParent = dropdownElement.parentElement;
            const selectedElement = dropdownElement.getElementsByClassName(
                'selected',
            )[0] as HTMLButtonElement;

            if (scrollableParent && selectedElement) {
                scrollableParent.scrollTop =
                    selectedElement.offsetTop - selectedElement.clientHeight - 20;
            }
        }
    };

    const getLocalisedNames = (option: string) => {
        return countries[option as keyof typeof countries];
    };

    $: choices = choices.sort((a, b) =>
        getLocalisedNames(a)[language].localeCompare(getLocalisedNames(b)[language]),
    );

    $: {
        if (open) {
            scrollToSelectedCountry();
        } else {
            filter = '';
        }
    }

    $: {
        if (open && filterInputElement && autoFocusSearchField) {
            filterInputElement.focus();
        }
    }

    $: filteredChoices = choices.filter((countryCode) => {
        if (!filter.trim()) {
            return true;
        }

        return Object.values(getLocalisedNames(countryCode)).some(
            (name) => name.toLowerCase().indexOf(filter.toLowerCase()) !== -1,
        );
    });

    const borderRadius = getBorderRadius('dropdownItem');
</script>

<div class="relative flex items-center border-b border-t">
    <span class="absolute left-6">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
        >
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M9 3.5C5.96243 3.5 3.5 5.96243 3.5 9C3.5 12.0376 5.96243 14.5 9 14.5C10.519 14.5 11.893 13.8852 12.8891 12.8891C13.8852 11.893 14.5 10.519 14.5 9C14.5 5.96243 12.0376 3.5 9 3.5ZM2 9C2 5.13401 5.13401 2 9 2C12.866 2 16 5.13401 16 9C16 10.6625 15.4197 12.1906 14.4517 13.3911L17.7803 16.7197C18.0732 17.0126 18.0732 17.4874 17.7803 17.7803C17.4874 18.0732 17.0126 18.0732 16.7197 17.7803L13.3911 14.4517C12.1906 15.4197 10.6625 16 9 16C5.13401 16 2 12.866 2 9Z"
                fill="#9CA3AF"
            />
        </svg>
    </span>
    <input
        class="w-full border border-transparent py-4 pl-14 focus:border-blue-500 focus:outline-none focus:ring-0"
        placeholder="Search"
        type="text"
        bind:value={filter}
        bind:this={filterInputElement}
        on:keydown={(event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
            }
        }}
    />
</div>

<div
    class="my-3x pointer-events-auto max-h-96 w-full scroll-p-2 overflow-y-scroll"
    on:click|stopPropagation
    on:keydown
>
    {#if !!filteredChoices && filteredChoices.length > 0}
        <div class="grid gap-1 px-2 py-2" bind:this={dropdownElement}>
            {#each filteredChoices as option}
                {@const optionNames = getLocalisedNames(option)}

                <button
                    on:click|preventDefault={(event) => open && handleSelect(event, option)}
                    tabindex={open ? 0 : -1}
                    class={`${borderRadius} focus:border-1 flex items-center gap-2 px-3 py-2 text-sm font-normal transition-none hover:bg-gray-100 focus:border-blue-500 md:text-base`}
                    class:bg-blue-50={selected === option || value === option}
                    class:text-blue-500={selected === option || value === option}
                    class:border-blue-500={selected === option || value === option}
                    class:selected={selected === option || value === option}
                    class:text-gray-500={!(selected === option) && value !== option}
                    class:border-gray-200={!(selected === option) && value !== option}
                >
                    <span class="text-2xl">{getFlagEmoji(option)}</span>
                    <span>{optionNames[language]}</span>
                    {#if optionNames[language] !== optionNames.native}
                        <span>({getLocalisedNames(option).native})</span>
                    {/if}
                    <span class="ml-auto text-gray-400">+{getCountryCallingCode(option)}</span>
                </button>
            {/each}
        </div>
    {:else}<p class="p-5 text-sm text-gray-500">{translations[language].noCountryFound}</p>{/if}
</div>
